import { useCallback, useEffect, useState } from 'react'
import { makeAssistantToolUI, type ToolCallMessagePartProps } from '@assistant-ui/react'
import { usePlaidLink } from 'react-plaid-link'

import { createLinkSession, completeLink } from '../api/generated/fluence'

type ConnectBankResult = { connectionId: string; institution?: string }

/**
 * Opens the bank's login and reports back the connection it produced.
 *
 * The one tool that does its own work rather than returning data for the server to act on,
 * because it has no choice: the widget is a browser thing, and the short-lived public token
 * it hands back exists only here. Exchanging it from the click also keeps it to exactly
 * once — replaying the transcript cannot re-run it, which matters because a public token is
 * single-use and a second exchange fails.
 *
 * What goes back into the conversation is only the connection's id. The server re-reads the
 * accounts from the provider rather than trusting anything the browser says about them.
 */
export const ConnectBankToolUI = makeAssistantToolUI<Record<string, never>, ConnectBankResult>({
  toolName: 'connectBank',
  render: ConnectBankTool,
})

function ConnectBankTool({
  status,
  result,
  addResult,
}: ToolCallMessagePartProps<Record<string, never>, ConnectBankResult>) {
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const waiting = status.type === 'requires-action'

  const onSuccess = useCallback(
    async (publicToken: string) => {
      setBusy(true)
      setError(null)
      try {
        const response = await completeLink({ publicToken })
        if (response.status === 201) {
          addResult({
            connectionId: response.data.connection.id,
            institution: response.data.connection.institutionName ?? undefined,
          })
        } else {
          setError('Your bank connected, but Fluence could not finish the link.')
        }
      } catch {
        setError('Your bank connected, but Fluence could not finish the link.')
      } finally {
        setBusy(false)
        setLinkToken(null)
      }
    },
    [addResult],
  )

  const { open, ready } = usePlaidLink({
    token: linkToken ?? '',
    onSuccess: (publicToken: string | null) => {
      if (publicToken) {
        void onSuccess(publicToken)
      } else {
        setLinkToken(null)
        setError('Your bank returned an incomplete response. Please try again.')
      }
    },
    // Abandoning the bank's login is an ordinary thing to do, not a failure.
    onExit: () => setLinkToken(null),
  })

  // Opening is a side effect, and Plaid needs the token first — which takes a round trip,
  // so it cannot happen inside the click handler either.
  useEffect(() => {
    if (linkToken && ready && !busy) open()
  }, [linkToken, ready, busy, open])

  const begin = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const response = await createLinkSession()
      if (response.status === 200) setLinkToken(response.data.linkToken)
      else setError('Could not start a bank connection.')
    } catch {
      setError('Could not start a bank connection.')
    } finally {
      setBusy(false)
    }
  }, [])

  if (result) {
    return (
      <p className="aui-note">
        Connected{result.institution ? ` to ${result.institution}` : ''}.
      </p>
    )
  }

  return (
    <div className="aui-action">
      <button
        type="button"
        className="btn-primary"
        onClick={() => void begin()}
        disabled={busy || !waiting}
      >
        {busy ? 'Connecting…' : 'Connect my bank'}
      </button>
      {error && <p className="notice is-error">{error}</p>}
    </div>
  )
}
